import type { FC, FormEvent, ReactNode } from 'react'

import { useMemo, useRef, useState } from 'react'
import Link from 'next/link'

import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { AcidButton } from '@/components/generals/acid-button'

type Status = 'idle' | 'sending' | 'sent' | 'error'

interface FieldProps {
    id: string
    name: string
    placeholder: string
    type?: string
    textarea?: boolean
    value: string
    onChange: (value: string) => void
}

const Field: FC<FieldProps> = ({
    id,
    name,
    placeholder,
    type = 'text',
    textarea = false,
    value,
    onChange
}): ReactNode => {
    const [focused, setFocused] = useState(false)

    const shared = {
        id,
        name,
        required: true,
        value,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
        onChange: (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => onChange(event.target.value),
        className:
            'w-full bg-transparent pb-3 pt-6 text-[16px] font-semibold uppercase tracking-[-0.04em] text-bone outline-none placeholder:text-smoke/70'
    }

    return (
        <div className='relative'>
            <label htmlFor={id} className='sr-only'>
                {placeholder}
            </label>

            {textarea ? (
                <textarea {...shared} rows={4} placeholder={placeholder} />
            ) : (
                <input {...shared} type={type} placeholder={placeholder} />
            )}

            <span
                aria-hidden='true'
                className='absolute inset-x-0 bottom-0 block h-px bg-smoke/40'
            />

            <span
                aria-hidden='true'
                className={cn(
                    'absolute inset-x-0 bottom-0 block h-[2px] origin-left bg-acid transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                    focused || value ? 'scale-x-100' : 'scale-x-0'
                )}
            />
        </div>
    )
}

export const ContactForm: FC = (): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')
    const formRef = useRef<HTMLFormElement | null>(null)

    const ready = useMemo(
        () =>
            name.trim().length > 1 &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
            message.trim().length > 4,
        [name, email, message]
    )

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!ready || status === 'sending') return

        setStatus('sending')
        setError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, message, website })
            })

            const payload = (await response.json()) as {
                ok: boolean
                error?: string
            }

            if (!response.ok || !payload.ok) {
                throw new Error(payload.error ?? 'Something went wrong.')
            }

            setStatus('sent')
            setName('')
            setEmail('')
            setMessage('')

            if (formRef.current) {
                gsap.fromTo(
                    formRef.current.querySelectorAll('[data-field]'),
                    { opacity: 1 },
                    {
                        opacity: 0.35,
                        duration: 0.4,
                        stagger: 0.06,
                        yoyo: true,
                        repeat: 1
                    }
                )
            }
        } catch (submitError) {
            setStatus('error')
            setError(
                submitError instanceof Error
                    ? submitError.message
                    : 'Something went wrong.'
            )
        }
    }

    return (
        <form ref={formRef} onSubmit={onSubmit} noValidate className='w-full'>
            <div className='flex flex-col gap-5'>
                <div data-field>
                    <Field
                        id='contact-name'
                        name='name'
                        placeholder='YOUR NAME'
                        value={name}
                        onChange={setName}
                    />
                </div>

                <div data-field>
                    <Field
                        id='contact-email'
                        name='email'
                        type='email'
                        placeholder='YOUR EMAIL'
                        value={email}
                        onChange={setEmail}
                    />
                </div>

                <div data-field>
                    <Field
                        id='contact-message'
                        name='message'
                        placeholder='MESSAGE'
                        textarea
                        value={message}
                        onChange={setMessage}
                    />
                </div>
            </div>

            <input
                type='text'
                name='website'
                value={website}
                tabIndex={-1}
                autoComplete='off'
                aria-hidden='true'
                onChange={(event) => setWebsite(event.target.value)}
                className='pointer-events-none absolute size-0 scale-0 opacity-0'
            />

            <div className='mt-9 flex flex-col gap-5 sm:flex-row sm:items-center'>
                <AcidButton
                    type='submit'
                    label={
                        status === 'sending'
                            ? 'Sending'
                            : status === 'sent'
                              ? 'Message sent'
                              : 'Submit'
                    }
                    disabled={!ready || status === 'sending'}
                />

                <p className='max-w-[38ch] text-[12px] font-semibold leading-[1.35] tracking-[-0.03em] text-ash'>
                    By submitting, you consent to my{' '}
                    <Link
                        href='/privacy-policy'
                        className='underline underline-offset-2 transition-colors duration-300 hover:text-acid'
                    >
                        Privacy Policy.
                    </Link>
                </p>
            </div>

            <p
                aria-live='polite'
                className={cn(
                    'mt-5 text-[14px] font-semibold tracking-[-0.03em] transition-opacity duration-500',
                    status === 'sent' && 'text-acid',
                    status === 'error' && 'text-[#ff6b6b]',
                    status !== 'sent' && status !== 'error' && 'opacity-0'
                )}
            >
                {status === 'sent' &&
                    'Message received. I read everything and reply to most of it.'}
                {status === 'error' && error}
                {status !== 'sent' && status !== 'error' && 'placeholder'}
            </p>
        </form>
    )
}
